export type PipelineDqxPhase = "idle" | "running" | "complete" | "failed";

export interface PipelineDqxRunOutcome {
  regionId?: string | null;
  runId: number;
  jobId: string;
  phase: PipelineDqxPhase;
  rowsPassing?: number | null;
  rowsWithIssues?: number | null;
  totalRowsChecked?: number | null;
  error?: string | null;
  message?: string | null;
  /** First file-global source_row_index for this mapping run (multi-table offset). */
  sourceRowIndexStart?: number | null;
}

export interface PipelineFileDqxState {
  phase: PipelineDqxPhase;
  runId: number | null;
  jobId: string;
  message?: string | null;
  error?: string | null;
  rowsPassing?: number | null;
  rowsWithIssues?: number | null;
  totalRowsChecked?: number | null;
  /** Per-table DQX runs when board Run DQ fired one job per region. */
  runs?: PipelineDqxRunOutcome[];
}

export const IDLE_PIPELINE_DQX_STATE: PipelineFileDqxState = {
  phase: "idle",
  runId: null,
  jobId: "",
};

export function getPipelineFileDqxState(
  dqxStateByFileId: Record<string, PipelineFileDqxState> | undefined,
  fileId: string
): PipelineFileDqxState {
  return dqxStateByFileId?.[fileId] ?? IDLE_PIPELINE_DQX_STATE;
}

/** True when this file has multiple tracked table-level DQ runs. */
export function isMultiTableDqxState(state: PipelineFileDqxState): boolean {
  return (state.runs?.length ?? 0) > 1;
}

/** True when any tracked table-level run hard-failed. */
export function dqxHasHardFailedRun(state: PipelineFileDqxState): boolean {
  return (state.runs ?? []).some((r) => r.phase === "failed");
}

/**
 * When blob + extraction JSON may be released after DQ.
 * Resolve cards (`complete` with issues) still need extraction for cleanse
 * re-runs and for View DQ after a refresh (preview reload).
 * Mixed complete + hard-failed tables also keep artifacts for DQ-dialog retry.
 */
export function shouldReleaseDqxArtifacts(state: PipelineFileDqxState): boolean {
  if (state.phase !== "complete" || (state.rowsWithIssues ?? 0) !== 0) return false;
  const runs = state.runs ?? [];
  if (runs.length > 0 && runs.some((r) => r.phase !== "complete")) return false;
  return true;
}

/**
 * Re-derive file-level phase/counts from `runs` when present so stale
 * `phase: "failed"` on a mixed multi-table state cannot keep Run DQ visible.
 */
export function normalizePipelineDqxState(state: PipelineFileDqxState): PipelineFileDqxState {
  if (!state.runs || state.runs.length === 0) return state;
  return aggregatePipelineDqxRuns(state.runs);
}

/**
 * Derive pass/fail display counts when passing was null but total + issues are known.
 */
export function resolveDqxPassFailCounts(params: {
  rowsPassing?: number | null;
  rowsWithIssues?: number | null;
  totalRowsChecked?: number | null;
}): { rowsPassing: number | null; rowsWithIssues: number | null } {
  const issues = params.rowsWithIssues ?? null;
  const total = params.totalRowsChecked ?? null;
  let passing = params.rowsPassing ?? null;
  if (passing == null && total != null && issues != null) {
    passing = Math.max(0, total - issues);
  }
  return { rowsPassing: passing, rowsWithIssues: issues };
}

/**
 * Aggregate per-run outcomes into file-level DQ state.
 * - running while any run is running
 * - complete when at least one table completed (including mixed hard-fail)
 * - failed only when every run hard-failed
 * Primary runId/jobId prefers a run with issues, else the last successful/last run.
 */
export function aggregatePipelineDqxRuns(runs: PipelineDqxRunOutcome[]): PipelineFileDqxState {
  if (runs.length === 0) return { ...IDLE_PIPELINE_DQX_STATE };

  const anyRunning = runs.some((r) => r.phase === "running" || r.phase === "idle");
  const anyFailed = runs.some((r) => r.phase === "failed");
  const anyComplete = runs.some((r) => r.phase === "complete");

  let phase: PipelineDqxPhase = "running";
  if (anyRunning) phase = "running";
  else if (anyComplete) phase = "complete";
  else phase = "failed";

  const terminal = !anyRunning;
  const rowsWithIssues = terminal
    ? runs.reduce((sum, r) => sum + (r.rowsWithIssues ?? 0), 0)
    : null;
  const totalRowsChecked = terminal
    ? runs.every((r) => r.totalRowsChecked != null)
      ? runs.reduce((sum, r) => sum + (r.totalRowsChecked ?? 0), 0)
      : null
    : null;
  const rowsPassing = terminal
    ? runs.every(
        (r) => r.rowsPassing != null || (r.totalRowsChecked != null && r.rowsWithIssues != null)
      )
      ? runs.reduce((sum, r) => {
          const resolved = resolveDqxPassFailCounts({
            rowsPassing: r.rowsPassing,
            rowsWithIssues: r.rowsWithIssues,
            totalRowsChecked: r.totalRowsChecked,
          });
          return sum + (resolved.rowsPassing ?? 0);
        }, 0)
      : totalRowsChecked != null && rowsWithIssues != null
        ? Math.max(0, totalRowsChecked - rowsWithIssues)
        : null
    : null;

  const withIssues = [...runs].reverse().find((r) => (r.rowsWithIssues ?? 0) > 0);
  const lastSuccess = [...runs].reverse().find((r) => r.phase === "complete");
  const lastFailed = [...runs].reverse().find((r) => r.phase === "failed");
  const primary = withIssues ?? lastSuccess ?? lastFailed ?? runs[runs.length - 1]!;

  return {
    phase,
    runId: primary.runId,
    jobId: primary.jobId,
    runs,
    rowsPassing,
    rowsWithIssues,
    totalRowsChecked,
    error: anyFailed ? "Data quality checks failed for one or more tables." : null,
    message: anyRunning
      ? `Validating ${runs.filter((r) => r.phase === "complete").length} of ${runs.length}…`
      : (primary.message ?? null),
  };
}
